import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  Self,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PaperlessPreferenceFieldsetComponent,
  ProtectionStartSummaryPanelComponent,
  TermsAndConditionsFieldsetComponent,
} from '@checkout/ui-components';
import {
  CheckoutDirectDebitMethod,
  CheckoutFeature,
  DirectDebitFormValue,
} from '@checkout/util-models';
import { HasSelectedProductTypeDirective } from '@domgen/dgx-fe-basket';
import { ProductType, filterNullUndefined } from '@domgen/dgx-fe-common';
import { ButtonComponent } from '@domgen/dgx-fe-components/button';
import { ContentComponent } from '@domgen/dgx-fe-components/content';
import {
  InterpolatePipe,
  TestHookDirective,
} from '@domgen/dgx-fe-components/general';
import { FeatureEnabledDirective } from '@domgen/dgx-fe-config';
import { CheckboxComponent } from '@domgen/dgx-fe-forms/checkbox';
import {
  NumberInputComponent,
  SortcodeInputComponent,
  TextInputComponent,
} from '@domgen/dgx-fe-forms/input';
import {
  FormFieldComponent,
  FormFieldDirective,
  FormFieldsetComponent,
  FormFieldsetDirective,
  FormRowComponent,
  FormSubmitButtonComponent,
} from '@domgen/dgx-fe-forms/layout';
import { SelectComponent } from '@domgen/dgx-fe-forms/select';
import {
  FormDisabledDirective,
  FormSubmitDirective,
} from '@domgen/dgx-fe-forms/shared';
import {
  GAContainerDirective,
  GAFormFieldDirective,
  GAFormSubmitDirective,
} from '@domgen/dgx-fe-ga';

import { AccordionPanelComponent } from '@domgen/dgx-fe-components/accordion';
import {
  COUNTRY_ISO_NUMBER,
  CountryIsoNumber,
  TranslatePipe,
} from '@domgen/dgx-fe-i18n';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';
import { first } from 'rxjs';
import { DirectDebitTestHooks } from '../../models/test/direct-debit-test-hooks.enum';
import { DirectDebitFormComponentService } from '../../services/direct-debit-form-component/direct-debit-form.component.service';
import { DirectDebitGuaranteePanelComponent } from '../direct-debit-guarantee-panel/direct-debit-guarantee-panel.component';
import { UserDirectDebitDetailsComponent } from '../user-direct-debit-details/user-direct-debit-details.component';

@Component({
  selector: 'checkout-direct-debit-form',
  templateUrl: './direct-debit-form.component.html',
  styleUrls: ['./direct-debit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DirectDebitFormComponentService],
  standalone: true,
  imports: [
    AccordionPanelComponent,
    AsyncPipe,
    ButtonComponent,
    CheckboxComponent,
    ContentComponent,
    DirectDebitGuaranteePanelComponent,
    FeatureEnabledDirective,
    FormDisabledDirective,
    FormFieldComponent,
    FormFieldDirective,
    FormFieldsetComponent,
    FormFieldsetDirective,
    FormRowComponent,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    GAContainerDirective,
    GAFormFieldDirective,
    GAFormSubmitDirective,
    HasSelectedProductTypeDirective,
    InterpolatePipe,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NumberInputComponent,
    PaperlessPreferenceFieldsetComponent,
    ProtectionStartSummaryPanelComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    SelectComponent,
    SortcodeInputComponent,
    TermsAndConditionsFieldsetComponent,
    TestHookDirective,
    TextInputComponent,
    TranslatePipe,
    UserDirectDebitDetailsComponent,
  ],
})
export class DirectDebitFormComponent implements OnInit {
  @Output() submitted = new EventEmitter<DirectDebitFormValue>();

  directDebitMethod = CheckoutDirectDebitMethod;
  productType = ProductType;
  termsAndConditionsFeature = CheckoutFeature.TermsAndConditions;
  testHooks = DirectDebitTestHooks;
  showDirectDebitGuarantee =
    this.countryIsoNumber === CountryIsoNumber.GreatBritain;

  constructor(
    @Inject(COUNTRY_ISO_NUMBER) private countryIsoNumber: number,
    @Self() readonly cs: DirectDebitFormComponentService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.cs.buildForm();
  }

  onFormSubmit(formValue: DirectDebitFormValue): void {
    this.cs
      .validateAccountDetails(formValue)
      .pipe(first(), filterNullUndefined(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.submitted.emit(formValue);
      });
  }
}
